import '@terra-js/web-bridge';
import TerraKit from '@terra-js/terra-kit';
import type { ScreenConfig } from '@terra-js/shop-front-kit';
import type { IOrderService } from '@teko/order';
import type { GetLoyaltyConfigResult } from '@terra-js/terra-kit/dist/loyalty/types';

const ShopFrontKitLoadable = import('@terra-js/shop-front-kit');
const PaymentKitLoadable = import('@terra-js/payment-kit');

const waitForPaymentStatus = 'WAITING_FOR_PAYMENT_CONFIRMATION';
interface BasePayParams {
  customerProfileId: string;
  orderService: IOrderService;
}

interface OrderDetail {
  code: string;
  id: string;
  remainPayment: number;
}

interface PayForOrderParams extends BasePayParams {
  order: OrderDetail;
}
interface PayParams extends BasePayParams {
  orderCode: string;
  orderId: string;
  amount: number;
}

const terraHelper = {
  isAvailable: TerraKit.isAvailable,
  getLoyaltyConfig: (): Promise<GetLoyaltyConfigResult> => {
    return TerraKit.loyalty().getLoyaltyConfig();
  },
  handleBackOr: async (callback?: () => void) => {
    if (TerraKit.isAvailable()) {
      const ShopFrontKit = (await ShopFrontKitLoadable).default;
      const shouldHandleBack = await ShopFrontKit.shouldHandleBack();
      if (shouldHandleBack) {
        ShopFrontKit.handleBack();
      } else {
        callback?.();
      }
    } else {
      callback?.();
    }
  },
  getScreenConfig: async (): Promise<ScreenConfig | undefined> => {
    if (TerraKit.isAvailable()) {
      const ShopFrontKit = (await ShopFrontKitLoadable).default;
      return ShopFrontKit.getScreenConfig();
    } else {
      return Promise.resolve(undefined);
    }
  },
  finish: () => TerraKit.finish(),
  payForOrder: ({
    order,
    customerProfileId,
    orderService,
  }: PayForOrderParams): Promise<void> => {
    return terraHelper.pay({
      orderCode: order.code,
      orderId: order.id,
      amount: order.remainPayment,
      customerProfileId,
      orderService,
    });
  },
  pay: async ({
    orderCode,
    orderId,
    amount,
    customerProfileId,
    orderService,
  }: PayParams): Promise<void> => {
    if (TerraKit.isAvailable()) {
      const { default: PaymentKit, PaymentRequestBuilder } =
        await PaymentKitLoadable;
      const paymentRequestBuilder = new PaymentRequestBuilder();
      const { MainMethodType } = await import(
        '@terra-js/payment-kit/dist/constansts'
      );
      paymentRequestBuilder
        .setOrder({
          orderCode: orderCode,
          orderAmount: amount,
        })
        .setPaymentMethods({
          mainMethod: {
            method: MainMethodType.All,
            amount,
          },
          totalAmount: amount,
        })
        .setOptions({
          shouldShowPaymentResultScreen: true,
        });
      return PaymentKit.payForOrder(paymentRequestBuilder.build()).then(
        (result) => {
          switch (result.resultCode) {
            case 'succeeded':
              window.location.href = `/account/orders/${orderId}`;
              break;
            case 'canceled':
            case 'failed':
              orderService
                .getOrder({ orderId, customerId: customerProfileId })
                .then((order) => {
                  if (order.status === waitForPaymentStatus) {
                    window.location.href = `/confirm-payment/${orderId}`;
                  } else {
                    window.location.href = `/account/orders?tab=WAIT_PAYMENT`;
                  }
                })
                .catch(() => {
                  // this is a fallback in case the order is not synced with cov
                  window.location.href = `/account/orders?tab=WAIT_PAYMENT`;
                });
              break;
          }
        },
      );
    }
    return Promise.resolve();
  },
};

export default terraHelper;
